import React, {
  useState, useReducer, useMemo, useCallback, useRef, lazy, Suspense,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client'
import { Map } from 'immutable'
import { getBoundActions } from '../../../../../actions/index'
import { usePostHogEvent } from '../../../../PostHog/index'
import { useTranslatedText } from '../../../../StaticText/StaticTextContext'
import { selectAuth } from '../../../../../services/auth/selectors'
import { selectCurrentUserProfilePicture } from '../../../../../services/community/identity/profile/redux/selectors'
import { TYPE_UPDATE_PROFILE_IMAGE } from '../../../../../services/dialog/index'
import { setOverlayDialogVisible } from '../../../../../services/dialog/actions'
import { UPDATE_PROFILE } from '../../../../../services/community/query'
import BannersCommunity from '../BannersCommunity/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import ProfileGradientHeader from '../../shared/ProfileGradientHeader/index'
import ProfileAvatarRing from '../../shared/ProfileAvatarRing/index'
import ProfileMetaLine from '../../shared/ProfileMetaLine/index'
import Camera from '../../../../Icon/icons/Camera/index'
import EyeShowSolid from '../../../../Icon/icons/EyeShowSolid/index'
import ProfileShareButton from '../../shared/ProfileShareButton/index'
import type {
  UpdateProfileResponse,
  UserProfile,
  SettingsFormState,
  SettingsFormFieldAction,
} from '../../../../../services/community/types'
import { MAX_ARCHETYPES } from '../../../../../services/community/identity/profile/constants'
import {
  FORM_FIELD,
  formReducer,
  buildInitialFormState,
} from '../../../../../services/community/identity/settings/settings-form-reducer'
import {
  isProfileSettingsDirty,
  type ProfileSettingsInitialValues,
} from '../../../../../services/community/identity/settings/settings-dirty'
import { getMemberSinceYear } from '../../../../../services/community/identity/library/content-utils'
import createProfileCacheUpdater from '../../../../../services/community/identity/profile/cache-updaters'

import ProfileInformationCard from './sections/ProfileInformationCard'
import VibeLineSection from './sections/VibeLineSection'
import InterestsCard from './sections/InterestsCard'
import PrivacyCard from './sections/PrivacyCard'
import SettingsSaveBar from './sections/SettingsSaveBar'
// TODO: re-enable NotificationsCard when notification preferences are wired to the backend
// import NotificationsCard from './sections/NotificationsCard'

const ConnectionsModal = lazy(() => import('../../shared/ConnectionsModal/index'))

/**
 * Profile completeness score at the moment `community_profile_created` fires.
 *
 * Equal-weight across 7 user-filled fields. Each contributes 1/7 if filled.
 * Returns a number 0–1 (matches PostHog's convention for percentage
 * properties like `$prev_pageview_max_scroll_percentage`).
 *
 * Snapshot at activation time — does not auto-update on later edits
 * (subsequent saves fire `community_profile_edited`, not `_created`).
 */
const PROFILE_COMPLETENESS_FIELD_COUNT = 7

function computeProfileCompleteness (
  fs: SettingsFormState,
  hasAvatar: boolean,
): number {
  let filled = 0
  if (fs.bio) filled += 1
  if (hasAvatar) filled += 1
  if (fs.locationValue) filled += 1
  if (fs.birthdayValue) filled += 1
  if (fs.selectedArchetypes.length > 0) filled += 1
  if (fs.journeyStage) filled += 1
  if (fs.selectedTagIds.length > 0) filled += 1
  return filled / PROFILE_COMPLETENESS_FIELD_COUNT
}

export interface CommunityProfileSettingsFormProps {
  /**
   * The current user's profile. May be null when the query resolved with
   * no existing profile (new user) — the form treats that as a blank-slate
   * initialization driven by the Redux username.
   */
  profile: UserProfile | null
  availableTopicsJS: Array<{ id: number; label: string }>
  language: string
  history: { push: (path: string) => void; goBack: () => void }
}

/**
 * Profile settings form — seeded synchronously from Apollo profile data on
 * mount so the form paints populated on render 1 when the profile is
 * cached. The outer container (CommunityProfileSettingsPage) only mounts
 * this component after both GET_CURRENT_PROFILE and GET_CANONICAL_CENTERS
 * have settled, and keys the mount on profile.uid so in-flight user edits
 * are preserved across background refetches.
 */
function CommunityProfileSettingsForm ({
  profile,
  availableTopicsJS,
  language,
  history,
}: CommunityProfileSettingsFormProps) {
  const auth = useSelector(selectAuth)
  const currentUserPicture = useSelector(selectCurrentUserProfilePicture)
  const user = useSelector((state: { user?: Map<string, unknown> }) => state.user)
  const dispatch = useDispatch()
  const actions = getBoundActions(dispatch)

  // --- Static text ---
  const saveErrorText = useTranslatedText({ keys: ['saveError'] })
  const editProfilePhotoText = useTranslatedText({ keys: ['editProfilePhoto'] })
  const viewText = useTranslatedText({ keys: ['view'] })
  const shareText = useTranslatedText({ keys: ['share'] })
  const copyErrorText = useTranslatedText({ keys: ['copyError'] })
  const followingLabelText = useTranslatedText({ keys: ['followingLabel'] })
  const followerSingularText = useTranslatedText({ keys: ['followerSingular'] })
  const followerPluralText = useTranslatedText({ keys: ['followerPlural'] })
  const memberSinceYearTemplate = useTranslatedText({ keys: ['memberSinceYear'] })
  const locationLabelText = useTranslatedText({ keys: ['locationLabel'] })
  const birthdayLabelText = useTranslatedText({ keys: ['birthdayLabel'] })
  const connectionsIntroHeadingText = useTranslatedText({ keys: ['connectionsIntroHeading'] })

  const loggedInUid = auth.get('uid')
  const loggedInUuid = auth.get('uuid')
  const jwt = auth.get('jwt') as string | undefined

  // Apollo: mutation for profile save with cache update.
  // Cache vars MUST match the GET_CURRENT_PROFILE variables used by
  // CommunityProfileSettingsPage (includeProfileActivity: false) so that
  // readQuery/writeQuery hit the same cache key.
  const queryVars = {
    language,
    contentLanguage: [language],
    includeMemberFields: true,
    includeProfileActivity: false,
    includeWatchHistory: false,
  }
  const [updateProfileMutation] = useMutation<UpdateProfileResponse>(
    UPDATE_PROFILE,
    {
      context: { jwt },
      update: createProfileCacheUpdater(queryVars),
    },
  )

  // --- Save guard (prevents double-submit across all async steps) ---
  const savingRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)

  // --- Form state ---
  // Seeded synchronously from profile + user + availableTopicsJS on mount.
  // The outer container gates on profileLoading=false AND tagOptionsReady,
  // so these inputs are always fully settled at init time. No useEffect
  // copy — the form paints populated on render 1 when data is cached.
  const initArgs = {
    profile,
    profileDataPresent: true,
    user,
    availableTopicsJS,
    tagOptionsReady: true,
  }
  const [formState, dispatchForm] = useReducer(
    formReducer,
    initArgs,
    (a) => buildInitialFormState(a).formState,
  )

  // Baseline snapshot for dirty detection. Lazy-initialized on first render
  // from the same inputs as the reducer; updated on save-success.
  const initialValuesRef = useRef<ProfileSettingsInitialValues | null>(null)
  if (initialValuesRef.current === null) {
    initialValuesRef.current = buildInitialFormState(initArgs).initialValues
  }

  const {
    displayName, bio, locationValue, birthdayValue, selectedTagIds,
    profileVisibility, locationVisibility, birthdayVisibility,
    memberSinceVisibility, bioVisibility, topicsVisibility, archetypesVisibility,
    journeyStageVisibility, selectedArchetypes, journeyStage,
  } = formState

  // Ref keeps handleSave stable — it reads current form values without
  // needing them in its dependency array.
  const formStateRef = useRef(formState)
  formStateRef.current = formState

  // --- Archetype toggle handler ---
  const handleArchetypeToggle = useCallback((key: string) => {
    let next: string[]
    if (selectedArchetypes.includes(key)) {
      next = selectedArchetypes.filter((k) => k !== key)
    } else if (selectedArchetypes.length < MAX_ARCHETYPES) {
      next = [...selectedArchetypes, key]
    } else {
      next = selectedArchetypes
    }
    dispatchForm({
      type: FORM_FIELD,
      field: 'selectedArchetypes',
      value: next,
    })
  }, [selectedArchetypes])

  // --- Journey change handler ---
  const handleJourneyChange = useCallback((key: string) => {
    dispatchForm({ type: FORM_FIELD, field: 'journeyStage', value: key })
  }, [])

  const trackProfileEdited = usePostHogEvent('community_profile_edited')
  // Activation event — fires only on the *first* successful save when the user
  // had no profile at mount. Subsequent saves only fire community_profile_edited.
  const trackProfileCreated = usePostHogEvent('community_profile_created')
  const wasNewProfileRef = useRef(!profile)
  const profileCreatedFiredRef = useRef(false)
  // Profile-share tracking fires here (Redux is in scope) via the
  // ProfileShareButton's `onShared` prop. The button is presentational so
  // its unit tests can render without a Redux Provider.
  const trackProfileShared = usePostHogEvent('community_profile_shared')
  const handleProfileShared = useCallback((method: 'copy_link') => {
    // Settings page is self-only — the target of the share is the actor's
    // own profile. Capture display name (not UUID) for privacy.
    trackProfileShared(method, displayName || undefined)
  }, [trackProfileShared, displayName])

  // --- Save handler ---
  // Reads form values from formStateRef so the callback stays stable
  // across keystrokes — only truly stable deps in the array.
  const handleSave = useCallback(async () => {
    const ivEarly = initialValuesRef.current
    const fsEarly = formStateRef.current
    if (!ivEarly || !isProfileSettingsDirty(fsEarly, ivEarly)) return
    if (savingRef.current) return
    savingRef.current = true
    setIsSaving(true)
    try {
      const fs = formStateRef.current
      // Going private cascades all PUBLIC custom playlists → ANONYMOUS and
      // system playlists (favorites, watchLater) → PRIVATE on the backend
      // transactionally as part of userProfileUpdate. The cache stays
      // correct because every downstream consumer (PlaylistSharedPage,
      // useProfilePlaylists, SystemPlaylistFullView) is network-only and
      // re-fetches on its own mount. No client-side cascade needed.

      const ensureVis = (v: string, fallback: string): string => (
        (v === 'public' || v === 'private') ? v : fallback
      )
      const archetypeStr = fs.selectedArchetypes.join(',')
      const profileInput = {
        displayName: fs.displayName || null,
        bio: fs.bio || null,
        location: fs.locationValue || null,
        birthday: fs.birthdayValue || null,
        centerContentIds: fs.selectedTagIds.map(Number),
        profileVisibility: ensureVis(fs.profileVisibility, 'public'),
        locationVisibility: ensureVis(fs.locationVisibility, 'private'),
        birthdayVisibility: ensureVis(fs.birthdayVisibility, 'private'),
        memberSinceVisibility: ensureVis(fs.memberSinceVisibility, 'public'),
        bioVisibility: ensureVis(fs.bioVisibility, 'public'),
        topicsVisibility: ensureVis(fs.topicsVisibility, 'public'),
        archetypeVisibility: ensureVis(fs.archetypesVisibility, 'private'),
        yourJourneyVisibility: ensureVis(fs.journeyStageVisibility, 'private'),
        archetype: archetypeStr || null,
        yourJourney: fs.journeyStage || null,
      }
      await updateProfileMutation({ variables: { input: profileInput, language } })
      trackProfileEdited()
      if (wasNewProfileRef.current && !profileCreatedFiredRef.current) {
        profileCreatedFiredRef.current = true
        trackProfileCreated(
          !!fs.bio,
          !!currentUserPicture,
          fs.profileVisibility === 'private' ? 'private' : 'public',
          archetypeStr || undefined,
          computeProfileCompleteness(fs, !!currentUserPicture),
        )
      }

      // Stay on settings page after save — update baseline so dirty detection stays accurate
      initialValuesRef.current = {
        displayName: fs.displayName,
        bio: fs.bio,
        location: fs.locationValue,
        birthday: fs.birthdayValue,
        profileVisibility: fs.profileVisibility,
        locationVisibility: fs.locationVisibility,
        birthdayVisibility: fs.birthdayVisibility,
        memberSinceVisibility: fs.memberSinceVisibility,
        bioVisibility: fs.bioVisibility,
        topicsVisibility: fs.topicsVisibility,
        archetypesVisibility: fs.archetypesVisibility,
        journeyStageVisibility: fs.journeyStageVisibility,
        centerContentIds: fs.selectedTagIds.slice().sort().join(','),
        archetype: fs.selectedArchetypes.slice().sort().join(','),
        journeyStage: fs.journeyStage || '',
      }
    } catch {
      actions.toasty.addToasty(saveErrorText)
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }, [
    updateProfileMutation, language, saveErrorText, actions,
    currentUserPicture, trackProfileEdited, trackProfileCreated,
  ])

  const userAvatarUrl = useMemo(
    () => (currentUserPicture ? currentUserPicture.card || currentUserPicture.url || '' : ''),
    [currentUserPicture],
  )

  const handleEditAvatar = useCallback(() => {
    dispatch(setOverlayDialogVisible(TYPE_UPDATE_PROFILE_IMAGE))
  }, [dispatch])

  const handleViewProfile = useCallback(() => {
    if (loggedInUuid) history.push(`/profile/${loggedInUuid}`)
  }, [loggedInUuid, history])

  const handleSaveAndView = useCallback(async () => {
    await handleSave()
    handleViewProfile()
  }, [handleSave, handleViewProfile])

  const [connectionsTab, setConnectionsTab] = useState<'followers' | 'following' | 'discover' | null>(null)
  const handleOpenFollowers = useCallback(() => setConnectionsTab('followers'), [])
  const handleOpenFollowing = useCallback(() => setConnectionsTab('following'), [])
  const handleCloseConnections = useCallback(() => setConnectionsTab(null), [])

  // --- Stable per-field change handlers ---
  // Factory creates one handler per field, memoized so child components
  // receive stable references and skip unnecessary re-renders. Restricted
  // to keys whose state value is `string` so the dispatched value matches
  // the discriminated `SettingsFormFieldAction` branch for that key.
  type StringField = {
    [K in keyof SettingsFormState]: SettingsFormState[K] extends string ? K : never
  }[keyof SettingsFormState]
  type FieldHandler = (v: string) => void
  const fieldHandlers = useRef<Partial<Record<StringField, FieldHandler>>>({})
  const getFieldHandler = useCallback((field: StringField): FieldHandler => {
    let handler = fieldHandlers.current[field]
    if (!handler) {
      handler = (v: string) => {
        dispatchForm({ type: FORM_FIELD, field, value: v } as SettingsFormFieldAction)
      }
      fieldHandlers.current[field] = handler
    }
    return handler
  }, [])

  const onDisplayNameChange = getFieldHandler('displayName')
  const onBioChange = getFieldHandler('bio')
  const onLocationChange = getFieldHandler('locationValue')
  const onBirthdayChange = getFieldHandler('birthdayValue')
  const onBirthdayVisibilityChange = getFieldHandler('birthdayVisibility')
  const onMemberSinceVisibilityChange = getFieldHandler('memberSinceVisibility')
  const onBioVisibilityChange = getFieldHandler('bioVisibility')
  const onLocationVisibilityChange = getFieldHandler('locationVisibility')
  const onTopicsVisibilityChange = getFieldHandler('topicsVisibility')
  const onArchetypesVisibilityChange = getFieldHandler('archetypesVisibility')
  const onJourneyStageVisibilityChange = getFieldHandler('journeyStageVisibility')
  const onProfileVisibilityChange = getFieldHandler('profileVisibility')

  const isStealthMode = profileVisibility === 'private'
  const interestsVisible = !isStealthMode
    && topicsVisibility !== 'private'
    && archetypesVisibility !== 'private'
    && journeyStageVisibility !== 'private'

  const handleInterestsVisibilityChange = useCallback((visible: boolean) => {
    const v = visible ? 'public' : 'private'
    onTopicsVisibilityChange(v)
    onArchetypesVisibilityChange(v)
    onJourneyStageVisibilityChange(v)
  }, [onTopicsVisibilityChange, onArchetypesVisibilityChange, onJourneyStageVisibilityChange])

  const handleTagToggle = useCallback((
    updater: (prev: (string | number)[]) => (string | number)[],
  ) => {
    const next = updater(formStateRef.current.selectedTagIds)
    const value = next
      .map((v) => (typeof v === 'number' ? v : Number(v)))
      .filter((n) => !Number.isNaN(n))
    dispatchForm({ type: FORM_FIELD, field: 'selectedTagIds', value })
  }, [])

  const memberYear = profile ? getMemberSinceYear(profile.createdAt) : null
  const profileUid = profile?.uid || (typeof loggedInUid === 'string' ? loggedInUid : '')
  const followerCount = profile?.followerCount || 0
  const followingCount = profile?.followingCount || 0
  const followerLabel = followerCount === 1 ? followerSingularText : followerPluralText

  return (
    <div className="community-profile-settings">
      <ProfileGradientHeader
        className="community-profile-settings__gradient-header"
        avatarContent={(
          <Button
            type={BUTTON_TYPES.GHOST}
            className="community-profile-settings__hero-avatar-btn"
            onClick={handleEditAvatar}
            aria-label={editProfilePhotoText}
          >
            <ProfileAvatarRing avatarUrl={userAvatarUrl} displayName={displayName} />
            <span className="community-profile-settings__hero-avatar-overlay">
              <Camera />
            </span>
          </Button>
        )}
        displayName={displayName}
        actionButtons={(
          <>
            <Button
              type={BUTTON_TYPES.GHOST}
              className="profile-gradient-header__action-btn profile-gradient-header__view-btn"
              onClick={handleViewProfile}
            >
              <EyeShowSolid size="16px" />
              {viewText}
            </Button>
            <ProfileShareButton
              uid={String(loggedInUuid)}
              label={shareText}
              errorMessage={copyErrorText}
              onShared={handleProfileShared}
            />
          </>
        )}
        socialLine={(
          <span className="profile-gradient-header__social-counts">
            <button
              type="button"
              className="profile-gradient-header__social-link"
              onClick={handleOpenFollowers}
              data-testid="gradient-header-followers-link"
            >
              {followerCount}
              {' '}
              {followerLabel}
            </button>
            <span className="profile-gradient-header__social-dot" aria-hidden="true" />
            <button
              type="button"
              className="profile-gradient-header__social-link"
              onClick={handleOpenFollowing}
              data-testid="gradient-header-following-link"
            >
              {followingCount}
              {' '}
              {followingLabelText}
            </button>
          </span>
        )}
        metaLine={(
          <ProfileMetaLine
            memberYear={memberYear}
            memberSinceLabel={memberSinceYearTemplate}
            location={locationValue || undefined}
            locationLabel={locationLabelText}
            showLocation={!!locationValue}
            birthday={birthdayValue || undefined}
            birthdayLabel={birthdayLabelText}
            showBirthday={!!birthdayValue}
          />
        )}
        bio={bio}
      />
      <div className="community-profile-settings__container">
        <div className="community-profile-settings__intro-heading">
          {connectionsIntroHeadingText}
        </div>

        {/* Privacy */}
        <PrivacyCard
          profileVisibility={profileVisibility}
          onProfileVisibilityChange={onProfileVisibilityChange}
        />

        {/* Information */}
        <ProfileInformationCard
          displayName={displayName}
          onDisplayNameChange={onDisplayNameChange}
          location={locationValue}
          onLocationChange={onLocationChange}
          locationVisibility={locationVisibility}
          onLocationVisibilityChange={onLocationVisibilityChange}
          birthday={birthdayValue}
          onBirthdayChange={onBirthdayChange}
          birthdayVisibility={birthdayVisibility}
          onBirthdayVisibilityChange={onBirthdayVisibilityChange}
          memberSince={profile?.createdAt}
          memberSinceVisibility={memberSinceVisibility}
          onMemberSinceVisibilityChange={onMemberSinceVisibilityChange}
          profileVisibility={profileVisibility}
        />

        {/* Vibe Line */}
        <VibeLineSection
          bio={bio}
          onBioChange={onBioChange}
          bioVisibility={bioVisibility}
          onBioVisibilityChange={onBioVisibilityChange}
          profileVisibility={profileVisibility}
          renderModal={actions.dialog.renderModal}
        />

        {/* Interests — Journey Stage + Topics + Archetypes */}
        <InterestsCard
          availableTopics={availableTopicsJS}
          selectedTagIds={selectedTagIds}
          onTagToggle={handleTagToggle}
          selectedArchetypes={selectedArchetypes}
          onArchetypeToggle={handleArchetypeToggle}
          journeyStage={journeyStage}
          onJourneyChange={handleJourneyChange}
          interestsVisible={interestsVisible}
          onInterestsVisibilityChange={handleInterestsVisibilityChange}
          profileVisibility={profileVisibility}
        />

        {/* Bottom save bar — Cancel + Save & View */}
        <SettingsSaveBar
          onCancel={handleViewProfile}
          onSaveAndView={handleSaveAndView}
          saving={isSaving}
        />

        {/*
          TODO: re-enable NotificationsCard when notification
          preferences are wired to the backend
        */}
        {/* <NotificationsCard /> */}

      </div>
      <BannersCommunity language={language} />
      {connectionsTab && profileUid ? (
        <Suspense fallback={null}>
          <ConnectionsModal
            uid={profileUid}
            initialTab={connectionsTab}
            onClose={handleCloseConnections}
            jwt={jwt}
            displayName={displayName || auth.get('username') || ''}
          />
        </Suspense>
      ) : null}
    </div>
  )
}

export default CommunityProfileSettingsForm
